import {useContext, useState, useRef} from "react";
import {StateContext} from "../Contexts";
import ButtonPrimaryTransparent from "@erecht24/frontend-kit/theme/components/buttons/ButtonPrimaryTransparent";
import {
    finalizePaymentData,
    getCurrentPlanVariant,
    getPaymentConfig,
    makeOrderPayload
} from "../Billwerk/SubscriptionJS";
import validate, {validateCreditCardPaymentData, validateSepaPaymentData} from "../Util/validate";
import handleError from "../Billwerk/handleError";
import LegalText from "./LegalText";
import {track} from "../Util/tracking";
import {
    PAYMENT_OPTION_CREDITCARD,
    PAYMENT_OPTION_PAYPAL,
    PAYMENT_OPTION_SEPA,
    PAYMENT_OPTION_SEPA_XML
} from "../Constants";
import FriendlyCaptcha from "./FriendlyCaptcha";

export default function ({upgrade = false}) {
    /** @type CheckoutState */
    const state = useContext(StateContext);
    const [processing, setProcessing] = useState(false);
    const captchaRef = useRef(null);
    const [captchaSolution, setCaptchaSolution] = useState(null);
    const [sitekey] = useState(document.getElementById('checkout-script').getAttribute('data-sitekey'));

    const resetCaptcha = () => {
        setCaptchaSolution(null);
        captchaRef.current?.reset();
    };

    const onOrderClick = async () => {
        let validationFailed = false;
        let validation = await validate(state.customerData);

        if (validation.length > 0) {
            state.setCustomerDataValidation(validation);
            validationFailed = true;
        }

        if (state.paymentOption?.key === PAYMENT_OPTION_SEPA.key || state.paymentOption?.key === PAYMENT_OPTION_SEPA_XML.key) {
            let sepaValidation = validateSepaPaymentData(state.paymentData, true);

            if (sepaValidation.length > 0) {
                state.setPaymentDataValidation(sepaValidation);
                validationFailed = true;
            }
        } else if (state.paymentOption?.key === PAYMENT_OPTION_CREDITCARD.key) {
            let ccValidation = validateCreditCardPaymentData(state.paymentData, true);

            if (ccValidation.length > 0) {
                state.setPaymentDataValidation(ccValidation);
                validationFailed = true;
            }
        }

        if (validationFailed) {
            setProcessing(false);
            return;
        }

        let billwerkService = (upgrade && !state.paymentProviderChangeModeEnabled) ? null : new SubscriptionJS.Signup();
        let paymentService = new SubscriptionJS.Payment(getPaymentConfig(state.mainProps.entityId, state.returnUrl),
            () => {
                createOrder();
            },
            (error) => {
                console.log(error);
                createOrder();
            }
        );

        const createOrder = function () {
            const cart = makeOrderPayload(
                state,
                state.customerData,
                upgrade ? (state.contractData?.contract?.Id ?? null) : null,
                upgrade ? state.cartViewData?.changeDate : null
            );
            const cartWithCaptcha = {
                ...cart,
                captcha_solution: captchaSolution
            };

            fetch('//' + window.location.host + '/orders', {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                },
                body: JSON.stringify(cartWithCaptcha)
            })
                .then((response) => {
                    if (!response.ok) {
                        return Promise.reject(response);
                    }

                    return response.json();
                }, errorHandler)
                .then((data) => pay(data), errorHandler);
        };

        const pay = function (order) {
            let paymentData = {
                bearer: state.paymentOption.bearer,
                currency: "EUR"
            };

            sessionStorage?.setItem('checkout_payment_started', state.paymentOption?.key);

            if (state.paymentOption.key === PAYMENT_OPTION_SEPA.key || state.paymentOption.key === PAYMENT_OPTION_SEPA_XML.key || state.paymentOption.key === PAYMENT_OPTION_CREDITCARD.key) {
                Object.assign(paymentData, finalizePaymentData(state.paymentData));
            }

            if (upgrade && !state.paymentProviderChangeModeEnabled) {
                billwerkService = new SubscriptionJS.Portal(order.selfServiceToken.Token);
                billwerkService.upgradePayInteractive(paymentService, paymentData, {
                    OrderId: order.order.Id,
                    GrossTotal: order.order.TotalGross,
                    Currency: order.order.Currency
                }, paymentHandler, errorHandler)
            } else {
                billwerkService.paySignupInteractive(paymentService, paymentData, {
                    OrderId: order.order.Id,
                    GrossTotal: order.order.TotalGross > 0 ? order.order.TotalGross : 0,
                    Currency: order.order.Currency
                }, paymentHandler, errorHandler);
            }
        };

        const paymentHandler = function (result) {
            let planVariant = getCurrentPlanVariant(state);
            let contract_interval = '';
            let payment_interval = '';

            if(planVariant?.contract_period_quantity === 1 && planVariant?.contract_period_unit === 'Month')
                contract_interval = 'monthly';
            if(planVariant?.contract_period_quantity === 1 && planVariant?.contract_period_unit === 'Year')
                contract_interval = 'annually';

            if(planVariant?.fee_period_quantity === 1 && planVariant?.fee_period_unit === 'Month')
                payment_interval = 'monthly';
            if(planVariant?.fee_period_quantity === 1 && planVariant?.fee_period_unit === 'Year')
                payment_interval = 'annually';

            sessionStorage.setItem('contract_interval', contract_interval);
            sessionStorage.setItem('payment_interval', payment_interval);

            if (upgrade && !state.paymentProviderChangeModeEnabled) {
                billwerkService?.logout(() => true, () => true);
            }

            if (result.Url) {
                window.location.href = result.Url;
            } else {
                let returnUrl = new URL(state.returnUrl);
                returnUrl.searchParams.set('order_id', result.OrderId);
                window.location.href = returnUrl.href;
            }
        };

        const errorCallback = () => {
            resetCaptcha();
            setProcessing(false)
        };

        const errorHandler = function (errorData) {
            if (typeof errorData.json === 'function') {
                /**
                 * errorData is a failed Response object
                 * Try to parse json from the response and pass the result object to handleError
                 */
                errorData.json().then((data) => {
                    handleError(data, state, errorCallback);
                }, () => {
                    /**
                     * Parsing json from response failed, call handleError with a generic errorCode
                     */
                    handleError({
                        errorCode: ["UnmappedError"]
                    }, state, errorCallback);
                })
            } else {
                /**
                 * errorHandler was called with a proper errorData object, pass it to handleError
                 */
                handleError(errorData, state, errorCallback);
            }
        };
    };

    const startOrder = () => {
        setProcessing(true);
        track(
            onOrderClick,
            'checkout',
            'submit_order',
            'order_now'
        );
    };

    return <>
        <LegalText></LegalText>

        <div className="flex items-center justify-center mb-10">
            <FriendlyCaptcha
                ref={captchaRef}
                sitekey={sitekey}
                startMode={"auto"}
                onComplete={(solution) => setCaptchaSolution(solution)}
                onError={(err) => {
                    setCaptchaSolution(null);
                    console.error(err);
                }}
                onExpire={() => setCaptchaSolution(null)}
            />
        </div>

        <div className="text-center">
            <ButtonPrimaryTransparent onClick={startOrder}
                fullWidth={true}
                disabled={!state.paymentOption || processing || !captchaSolution}>
                {processing &&
                    <>...</>
                }
                {!processing &&
                    <>{upgrade ? 'Jetzt Tarifwechsel abschließen' : 'Jetzt kostenpflichtig bestellen'}</>
                }
            </ButtonPrimaryTransparent>
            {(state.paymentOption?.key === PAYMENT_OPTION_PAYPAL.key) && (
                <p className="text-base text-graphite mt-3">Sie werden zur Bezahlung weitergeleitet.</p>
            )}
        </div>
    </>
}
